import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import Modal from '../../components/common/Modal';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import { HiOutlinePlus, HiOutlineCash, HiOutlineCheckCircle, HiOutlineClock, HiOutlineXCircle } from 'react-icons/hi';

const STATUS_STYLES = {
  pending:  { variant: 'warning', label: 'বকেয়া',   icon: <HiOutlineClock /> },
  approved: { variant: 'success', label: 'অনুমোদিত', icon: <HiOutlineCheckCircle /> },
  rejected: { variant: 'danger',  label: 'প্রত্যাখ্যাত', icon: <HiOutlineXCircle /> },
};

const WorkerAdvance = () => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ amount: '', reason: '' });
  const [success, setSuccess] = useState('');
  const { t } = useLanguage();

  useEffect(() => { fetchAdvances(); }, []);

  const fetchAdvances = async () => {
    try { const { data } = await API.get('/worker/advance/my'); setAdvances(data.advances); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess('');
    setSubmitting(true);
    try {
      await API.post('/worker/advance', { amount: Number(formData.amount), reason: formData.reason });
      setFormData({ amount: '', reason: '' });
      setModalOpen(false);
      setSuccess('অগ্রিম অনুরোধ পাঠানো হয়েছে — অ্যাডমিন অনুমোদনের পরে দেখবেন');
      fetchAdvances();
    } catch (error) {
      alert(error.response?.data?.message || 'অনুরোধ পাঠাতে ব্যর্থ');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const totalApproved = advances.filter((a) => a.status === 'approved').reduce((acc, a) => acc + Number(a.amount || 0), 0);
  const totalPending = advances.filter((a) => a.status === 'pending').reduce((acc, a) => acc + Number(a.amount || 0), 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('advances')}
        subtitle={`মোট ${advances.length} টি অনুরোধ`}
        action={
          <button onClick={() => setModalOpen(true)} data-welcome="request-advance" className="btn-primary">
            <HiOutlinePlus className="w-5 h-5" />
            <span className="hidden sm:inline">{t('requestAdvance')}</span>
          </button>
        }
      />

      {/* Success toast */}
      {success && (
        <div className="p-3 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 rounded-xl text-sm flex items-start gap-2 animate-slide-up">
          <HiOutlineCheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-brand-700 dark:text-brand-300 hover:opacity-70">✕</button>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="card text-center">
          <div className="text-3xl mb-1">⏳</div>
          <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">বকেয়া</p>
          <p className="text-xl font-bold text-accent-700 dark:text-accent-300 mt-1">৳{totalPending.toLocaleString('en-IN')}</p>
        </div>
        <div className="card text-center">
          <div className="text-3xl mb-1">✓</div>
          <p className="text-xs text-ink-500 dark:text-ink-400 uppercase tracking-wider">অনুমোদিত</p>
          <p className="text-xl font-bold text-brand-700 dark:text-brand-300 mt-1">৳{totalApproved.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* History list */}
      {advances.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="💸"
            title="কোনো অগ্রিম অনুরোধ নেই"
            description="অগ্রিম চাইলে উপরের বাটনে ক্লিক করুন"
            action={
              <button onClick={() => setModalOpen(true)} className="btn-primary">
                <HiOutlinePlus className="w-5 h-5" /> প্রথম অনুরোধ পাঠান
              </button>
            }
          />
        </div>
      ) : (
        <div className="space-y-3">
          {advances.map((adv) => {
            const style = STATUS_STYLES[adv.status] || STATUS_STYLES.pending;
            return (
              <div key={adv.id} className="card">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white flex-shrink-0">
                      <HiOutlineCash />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-ink-500 dark:text-ink-400">
                        {new Date(adv.created_at).toLocaleDateString('en-GB')}
                      </p>
                      <p className="text-base font-bold text-violet-700 dark:text-violet-300">
                        ৳{Number(adv.amount).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                  <Badge variant={style.variant} dot={adv.status === 'pending'}>
                    {style.label}
                  </Badge>
                </div>
                {adv.reason && (
                  <p className="text-sm text-ink-600 dark:text-ink-400 italic border-l-2 border-ink-200 dark:border-ink-700 pl-3 mt-2">
                    "{adv.reason}"
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Request modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={t('requestAdvance')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="input-label">{t('advanceAmount')} (৳)</label>
            <input
              type="number"
              min="1"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="input-field text-2xl font-bold"
              placeholder="1000"
              required
            />
          </div>
          <div>
            <label className="input-label">{t('advanceReason')}</label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="input-field"
              rows={3}
              placeholder="কেন অগ্রিম দরকার? (ঐচ্ছিক)"
            />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">{t('cancel')}</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? t('loading') : 'অনুরোধ পাঠান'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WorkerAdvance;
